import type { ReactNode } from "react";

/**
 * Turns one thing on or off (the brand system's `elements.switch`): ink when on, Arctic Haze in dark. A checkbox is only
 * for picking items from a set. With a `label` it is a row: the label and its hint on the left, the switch on the right.
 * Without one it stands alone and `aria-label` says what it switches.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  size = "md",
  disabled = false,
  title,
  className = "",
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  hint?: ReactNode;
  /** `sm` (36 × 20) in a toolbar or beside a filter; `md` (44 × 24) everywhere else. */
  size?: "md" | "sm";
  disabled?: boolean;
  title?: string;
  className?: string;
  "aria-label"?: string;
}) {
  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const knob = size === "sm" ? "size-4" : "size-5";
  const travel = size === "sm" ? "translate-x-4" : "translate-x-5";
  const control = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ? undefined : ariaLabel}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-haze-deep disabled:pointer-events-none disabled:opacity-40 ${track} ${
        checked ? "bg-ink dark:bg-haze" : "bg-fill-strong"
      }`}
    >
      <span
        className={`rounded-full shadow-sm transition-transform motion-reduce:transition-none ${knob} ${checked ? `${travel} bg-white dark:bg-ink` : "translate-x-0 bg-white"}`}
      />
    </button>
  );
  if (!label) return <span className={className}>{control}</span>;
  return (
    // The label clicks the switch, so the whole row toggles it.
    <label className={`flex items-center justify-between gap-4 text-sm ${disabled ? "" : "cursor-pointer"} ${className}`}>
      <span className="min-w-0">
        <span className="block">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-muted">{hint}</span>}
      </span>
      {control}
    </label>
  );
}
